import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon } from 'lucide-react';
import Settings from './Settings';
import PatchSettings from './PatchSettings';
import OsquerySettings from './OsquerySettings';
import AgentSettings from './AgentSettings';
import ComplianceRulesSettings from './ComplianceRulesSettings';
import { HubEmpty, HubSection } from './ui/HubShell';

type SettingsTab =
  | 'client-settings'
  | 'go-agent-config'
  | 'osquery'
  | 'patch'
  | 'compliance-rules'
  | 'integrations'
  | 'audit';

interface SettingsHubProps {
  initialTab?: string | null;
  clearInitialTab?: () => void;
}

const TABS: { id: SettingsTab; label: string }[] = [
  { id: 'client-settings', label: 'Client Settings' },
  { id: 'go-agent-config', label: 'Go Agent Config' },
  { id: 'osquery', label: 'Osquery' },
  { id: 'patch', label: 'Patch' },
  { id: 'compliance-rules', label: 'Compliance Rules' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'audit', label: 'Audit Log' },
];

const SettingsHub: React.FC<SettingsHubProps> = ({ initialTab, clearInitialTab }) => {
  const [activeTab, setActiveTab] = useState<SettingsTab>('client-settings');

  useEffect(() => {
    if (!initialTab) return;
    if (TABS.some((t) => t.id === initialTab)) {
      setActiveTab(initialTab as SettingsTab);
    }
    clearInitialTab?.();
  }, [initialTab, clearInitialTab]);

  return (
    <HubSection
      title="Settings"
      subtitle="Client settings, agent config, integrations, and audit."
      icon={<SettingsIcon size={20} />}
      eyebrow="Tenant"
      tabs={TABS}
      tab={activeTab}
      onTabChange={setActiveTab}
    >
      {activeTab === 'client-settings' && <Settings onClose={() => undefined} embedded />}
      {activeTab === 'go-agent-config' && <AgentSettings />}
      {activeTab === 'osquery' && <OsquerySettings />}
      {activeTab === 'patch' && <PatchSettings />}
      {activeTab === 'compliance-rules' && <ComplianceRulesSettings />}
      {activeTab === 'integrations' && (
        <HubEmpty
          title="Integrations"
          description="Connect external services and API keys here. Connectors will appear in this tab as they are enabled for the tenant."
        />
      )}
      {activeTab === 'audit' && (
        <HubEmpty
          title="Audit Log"
          description="Detailed admin and system actions will appear here when connected to the tenant audit API."
        />
      )}
    </HubSection>
  );
};

export default SettingsHub;
